/**
 * PublicRecordingService - Single-chunk recording for the public AI Scribe
 * iframe, now with Deepgram Nova-3 streaming transcription baked in.
 *
 * Architecture (mirrors PublicTranscriptionService for consistency):
 *   - One MediaStream from getUserMedia
 *   - One AudioContext + AnalyserNode (waveform)
 *   - One ScriptProcessor that:
 *       1. Sends 16 kHz mono PCM16 frames to the Deepgram WebSocket
 *       2. Buffers the same frames so we can wrap them in a WAV header
 *          for archival upload to Supabase
 *   - NO MediaRecorder. Avoids the MediaRecorder + AudioContext stall we
 *     hit when two sinks consumed the same stream simultaneously.
 *
 * Stop flow (the new architecture):
 *   1. Close the Deepgram socket gracefully and wait briefly for any
 *      trailing final transcripts.
 *   2. Assemble the WAV from the PCM buffer.
 *   3. POST /api/scribe/transcribe IMMEDIATELY with the accumulated
 *      transcription (no audioUrl). This is the fast call.
 *   4. Kick off the Supabase audio upload in the background — we don't
 *      wait for it. The audio is archived for later, but the gateway
 *      call already returned.
 */

import { aiScribeService } from './ai-scribe-service';
import { getKeyterms } from './transcription-commands';
import { supabaseStorageService } from './supabase-storage-service';

const BASE_PARAMS: Record<string, string> = {
  model: 'nova-3',
  encoding: 'linear16',
  sample_rate: '16000',
  channels: '1',
  interim_results: 'true',
  // Kept identical to public-transcription-service.ts on purpose — both
  // services share transcription-commands.ts and must see the same
  // tokenisation behaviour. smart_format is OFF because the "access"
  // wake word gets mangled to "X S" / "XCESS" when it's on.
  smart_format: 'false',
  punctuate: 'true',
  numerals: 'true',
  endpointing: '350',
  language: 'en-US',
  filler_words: 'false',
};

const TARGET_SAMPLE_RATE = 16000;
const KEEPALIVE_INTERVAL_MS = 5000;

function buildDeepgramUrl(keyterms: string[]): string {
  const params = new URLSearchParams(BASE_PARAMS);
  for (const term of keyterms) {
    if (term) params.append('keyterm', term);
  }
  return `wss://api.deepgram.com/v1/listen?${params.toString()}`;
}

export interface PublicRecordingConfig {
  appointmentId: string;
  patientId: string;
  locationId: string;
  scribeJobId: string;
  sectionTag: string;
  clientPlatform?: string;
  patientCode: string;
  authToken?: string;
}

export class PublicRecordingService {
  private apiKey: string;

  // Mic / audio graph
  private stream: MediaStream | null = null;
  private audioContext: AudioContext | null = null;
  private source: MediaStreamAudioSourceNode | null = null;
  private processor: ScriptProcessorNode | null = null;
  private analyser: AnalyserNode | null = null;
  private timeDomainArray: Float32Array | null = null;

  // Deepgram
  private socket: WebSocket | null = null;
  private keepAliveTimer: ReturnType<typeof setInterval> | null = null;

  // Recording state
  private isRecording = false;
  private isPaused = false;
  private recordingStartTime = 0;
  private totalPausedDuration = 0;
  private lastPauseTime = 0;

  // PCM buffer (single chunk for the whole session). Each entry is the
  // exact same 16 kHz PCM16 frame that just went to Deepgram.
  private pcmChunks: Int16Array[] = [];

  // Accumulated final transcript segments from Deepgram.
  private transcriptParts: string[] = [];

  // One-shot subscribers notified whenever a NEW final segment lands.
  // Used by flushPendingDeepgramFinals() on stop so the gateway call
  // waits for Deepgram's trailing final to actually arrive rather than
  // firing on a blind timeout.
  private transcriptListeners: Array<() => void> = [];

  // Waveform subscribers
  private audioLevelAnimationFrame: number | null = null;
  private audioLevelListeners: ((level: number) => void)[] = [];

  constructor(apiKey: string) {
    if (!apiKey) {
      throw new Error(
        'Deepgram API key is required (VITE_DEEPGRAM_API_KEY)'
      );
    }
    this.apiKey = apiKey;
  }

  // -------- Mic + audio graph --------------------------------------------

  private async initializeStream(): Promise<MediaStream> {
    if (this.stream) return this.stream;
    try {
      this.stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          channelCount: 1,
        },
      });
      this.audioContext = new AudioContext();
      this.audioContext.addEventListener('statechange', () => {
        const state = this.audioContext?.state;
        console.warn(
          '[PublicRecording] 🔊 AudioContext state changed →',
          state,
          { isRecording: this.isRecording, isPaused: this.isPaused }
        );
        if (state === 'suspended' && this.isRecording) {
          this.audioContext?.resume().catch(() => {
            /* noop */
          });
        }
      });
      this.source = this.audioContext.createMediaStreamSource(this.stream);
      this.analyser = this.audioContext.createAnalyser();
      this.analyser.fftSize = 256;
      this.analyser.smoothingTimeConstant = 0.8;
      this.timeDomainArray = new Float32Array(this.analyser.frequencyBinCount);
      this.source.connect(this.analyser);
    } catch (error) {
      console.error('❌ Failed to get microphone access:', error);
      throw new Error('Microphone access denied or unavailable');
    }
    return this.stream;
  }

  private attachAudioPipeline(stream: MediaStream): void {
    if (!this.audioContext) this.audioContext = new AudioContext();
    const inputRate = this.audioContext.sampleRate;
    if (!this.source) {
      this.source = this.audioContext.createMediaStreamSource(stream);
    }
    const bufferSize = 4096;
    this.processor = this.audioContext.createScriptProcessor(bufferSize, 1, 1);
    this.processor.onaudioprocess = (event) => {
      if (!this.isRecording || this.isPaused) return;
      const input = event.inputBuffer.getChannelData(0);
      const downsampled = downsampleFloat32(
        input,
        inputRate,
        TARGET_SAMPLE_RATE
      );
      const pcm16 = floatTo16BitPCM(downsampled);
      // Stream to Deepgram if the socket is up
      if (this.socket && this.socket.readyState === WebSocket.OPEN) {
        this.socket.send(pcm16);
      }
      // Always buffer into pcmChunks so we have audio to upload on stop
      this.pcmChunks.push(new Int16Array(pcm16));
    };
    this.source.connect(this.processor);
    this.processor.connect(this.audioContext.destination);
  }

  // -------- Deepgram socket ----------------------------------------------

  private openSocket(): Promise<void> {
    return new Promise((resolve, reject) => {
      const url = buildDeepgramUrl(getKeyterms());
      const socket = new WebSocket(url, ['token', this.apiKey]);
      socket.binaryType = 'arraybuffer';

      const onOpen = () => {
        socket.removeEventListener('open', onOpen);
        socket.removeEventListener('error', onErrorBeforeOpen);
        this.socket = socket;
        this.keepAliveTimer = setInterval(() => {
          if (socket.readyState === WebSocket.OPEN) {
            socket.send(JSON.stringify({ type: 'KeepAlive' }));
          }
        }, KEEPALIVE_INTERVAL_MS);
        resolve();
      };
      const onErrorBeforeOpen = () => {
        socket.removeEventListener('open', onOpen);
        socket.removeEventListener('error', onErrorBeforeOpen);
        reject(new Error('Failed to open Deepgram WebSocket'));
      };

      socket.addEventListener('open', onOpen);
      socket.addEventListener('error', onErrorBeforeOpen);
      socket.addEventListener('message', this.handleSocketMessage);
      socket.addEventListener('close', this.handleSocketClose);
    });
  }

  private handleSocketMessage = (event: MessageEvent) => {
    let payload: any;
    try {
      payload = JSON.parse(event.data as string);
    } catch {
      return;
    }
    if (payload?.type !== 'Results') return;
    const alt = payload?.channel?.alternatives?.[0];
    const text: string = alt?.transcript || '';
    if (!text || !payload.is_final) return;
    this.transcriptParts.push(text);
    // Notify any one-shot waiters (the stop-flow flush) that a final
    // segment just arrived. Iterate a copy — listeners remove themselves.
    for (const listener of [...this.transcriptListeners]) listener();
  };

  private handleSocketClose = (event: CloseEvent) => {
    const expected = event.code === 1000 || event.code === 1005;
    const fn = expected ? console.log : console.warn;
    fn(
      '[PublicRecording] Deepgram socket closed',
      `code=${event.code}`,
      event.reason || ''
    );
  };

  /**
   * Force Deepgram to emit a final for any audio it still has buffered,
   * then WAIT for that final (and any followup finals) to actually arrive
   * in handleSocketMessage before the caller builds the transcript.
   *
   * This is the guarantee that the gateway call includes the user's full
   * dictation up to the stop click — mirrors PublicTranscriptionService's
   * method of the same name.
   *
   * Strategy:
   *   1. Send {type:'Finalize'} — Deepgram flushes pending audio into
   *      final(s).
   *   2. Resolve on the NEXT final landing (one-shot transcript listener),
   *      OR a hard maxWaitMs timeout if nothing is pending (user was
   *      silent at stop time) so we never hang.
   *   3. A short grace period for any additional trailing finals to settle
   *      (Finalize can emit several finals if multiple sentence boundaries
   *      were pending).
   *
   * No-op if the socket isn't open (e.g. it dropped during a long pause) —
   * the caller proceeds with whatever finals it already has.
   */
  private async flushPendingDeepgramFinals(
    maxWaitMs = 1500,
    postFlushGraceMs = 250
  ): Promise<void> {
    const socket = this.socket;
    if (!socket || socket.readyState !== WebSocket.OPEN) return;
    try {
      socket.send(JSON.stringify({ type: 'Finalize' }));
    } catch (err) {
      console.warn(
        '[PublicRecording] Finalize send failed; proceeding with current buffer',
        err
      );
      return;
    }
    await new Promise<void>((resolve) => {
      let settled = false;
      const settleOnce = () => {
        if (settled) return;
        settled = true;
        window.clearTimeout(timer);
        const idx = this.transcriptListeners.indexOf(listener);
        if (idx > -1) this.transcriptListeners.splice(idx, 1);
        resolve();
      };
      // Resolve on the next final segment Deepgram emits — typically the
      // one produced by the Finalize we just sent.
      const listener = () => settleOnce();
      this.transcriptListeners.push(listener);
      // Safety net: if Deepgram had nothing to flush (silence at stop
      // time) no final will arrive, so resolve after maxWaitMs rather
      // than hanging the stop flow forever.
      const timer = window.setTimeout(settleOnce, maxWaitMs);
    });
    // Grace period for any followup finals — handleSocketMessage appends
    // each final synchronously, so a brief sleep captures the rest.
    await new Promise<void>((r) => window.setTimeout(r, postFlushGraceMs));
  }

  // -------- Public API ---------------------------------------------------

  async startRecording(): Promise<void> {
    if (this.isRecording) {
      console.warn('Recording is already in progress');
      return;
    }
    try {
      const stream = await this.initializeStream();

      // Reset session state
      this.pcmChunks = [];
      this.transcriptParts = [];
      this.recordingStartTime = Date.now();
      this.totalPausedDuration = 0;
      this.lastPauseTime = 0;

      await this.openSocket();
      this.attachAudioPipeline(stream);

      this.isRecording = true;
      this.isPaused = false;
      this.startAudioLevelDetection();

      // Defensive resume in case the iframe's AudioContext was created
      // suspended.
      if (this.audioContext && this.audioContext.state === 'suspended') {
        this.audioContext.resume().catch(() => {
          /* noop */
        });
      }
      console.log('✅ Started public recording (Deepgram + PCM capture)', {
        audioContextState: this.audioContext?.state,
        sampleRate: this.audioContext?.sampleRate,
      });
    } catch (error) {
      console.error('❌ Failed to start recording:', error);
      this.cleanup();
      throw error;
    }
  }

  async pauseRecording(): Promise<void> {
    if (!this.isRecording || this.isPaused) return;
    this.isPaused = true;
    this.lastPauseTime = Date.now();
    this.stopAudioLevelDetection();
    console.log('⏸️ Paused public recording');
  }

  async resumeRecording(): Promise<void> {
    if (!this.isRecording || !this.isPaused) return;
    this.isPaused = false;
    if (this.lastPauseTime > 0) {
      this.totalPausedDuration += Date.now() - this.lastPauseTime;
      this.lastPauseTime = 0;
    }
    this.startAudioLevelDetection();
    console.log('▶️ Resumed public recording');
  }

  /**
   * Stop recording and submit to the gateway.
   *
   *   1. Close Deepgram cleanly, wait briefly for trailing finals.
   *   2. POST /api/scribe/transcribe IMMEDIATELY with the accumulated
   *      transcription. NO audioUrl is sent — n8n uses the supplied
   *      transcript directly.
   *   3. In the background: assemble the WAV and upload it to Supabase
   *      for archival. The caller does NOT wait for this.
   */
  async stopRecording(
    config: PublicRecordingConfig,
    createdBy: string,
    dxList?: any
  ): Promise<{ partId?: string; partNumber?: number }> {
    if (!this.isRecording) {
      throw new Error('No active recording to stop');
    }
    // Flip the flag IMMEDIATELY so a concurrent second call (React strict
    // mode unmount + manual stop) is rejected by the guard above. This
    // is what protects against the duplicate Supabase audio upload that
    // showed up in DevTools Network as two .mpeg files with the same
    // filename.
    this.isRecording = false;

    this.stopAudioLevelDetection();

    // CRITICAL: before building the transcript for the gateway call we
    // must capture Deepgram's trailing final(s). Audio streamed right up
    // to the stop click is still being processed server-side; a blind
    // timeout here would drop the user's last words from the payload.
    // flushPendingDeepgramFinals() sends Finalize and then WAITS for the
    // resulting final to actually land (or a safety-net timeout if the
    // user was silent), so the API call is gated on the real final.
    await this.flushPendingDeepgramFinals();

    // Calculate duration excluding paused time.
    const totalElapsed = Date.now() - this.recordingStartTime;
    let pausedDuration = this.totalPausedDuration;
    if (this.isPaused && this.lastPauseTime > 0) {
      pausedDuration += Date.now() - this.lastPauseTime;
    }
    const durationSeconds = Math.round(
      Math.max(0, totalElapsed - pausedDuration) / 1000
    );

    // Assemble the transcript & snapshot the PCM buffer so we can tear
    // down the audio graph without losing audio for the background upload.
    const transcription = this.transcriptParts
      .join(' ')
      .replace(/\s+/g, ' ')
      .trim();
    const pcmChunksSnapshot = this.pcmChunks;
    this.pcmChunks = [];

    console.log('✅ Recording stopped — submitting transcript to gateway', {
      transcriptionLength: transcription.length,
      pcmChunkCount: pcmChunksSnapshot.length,
      durationSeconds,
    });

    if (!transcription) {
      // Without any transcription the gateway call would 400 (it now
      // requires `transcription`). Surface a clear error.
      this.cleanup();
      // isRecording already flipped at the top of this method.
      this.isPaused = false;
      throw new Error(
        'No transcription captured — please dictate again before stopping'
      );
    }

    // (a) Gateway call — no audioUrl in the new architecture.
    // locationId/appointmentId travel as explicit fields since n8n can
    // no longer derive them from the (now empty) audioUrl path.
    const response = await aiScribeService.uploadSectionAudioUrl('', {
      partNumber: 1,
      createdBy,
      scribeJobId: config.scribeJobId,
      sectionTag: config.sectionTag,
      isFinalPart: true,
      durationSeconds,
      dxList,
      locationId: config.locationId,
      appointmentId: config.appointmentId,
      clientPlatform: config.clientPlatform,
      patientCode: config.patientCode,
      authToken: config.authToken,
      transcription,
    });

    // (b) Fire-and-forget Supabase upload. The user has already moved on
    // by the time this finishes; we just want the audio archived.
    void this.uploadAudioInBackground(pcmChunksSnapshot, config);

    // Tear down the live audio pipeline. The background upload doesn't
    // need the AudioContext anymore — it operates on the snapshot.
    this.cleanup();
    // isRecording already flipped at the top of this method.
    this.isPaused = false;

    if (!response.success) {
      throw new Error(response.error || 'Gateway transcribe call failed');
    }
    console.log('✅ Gateway accepted transcription', response.data);
    return {
      partId: response.data?.partId,
      partNumber: response.data?.partNumber,
    };
  }

  private async uploadAudioInBackground(
    chunks: Int16Array[],
    config: PublicRecordingConfig
  ): Promise<void> {
    if (!chunks || chunks.length === 0) {
      console.warn(
        '[PublicRecording] No PCM data — skipping background audio upload'
      );
      return;
    }
    try {
      const totalSamples = chunks.reduce((sum, c) => sum + c.length, 0);
      if (totalSamples === 0) return;
      const combined = new Int16Array(totalSamples);
      let offset = 0;
      for (const c of chunks) {
        combined.set(c, offset);
        offset += c.length;
      }
      const wavBuffer = pcm16MonoToWav(combined, TARGET_SAMPLE_RATE);
      const filename = `${config.locationId}-${config.patientId}-${config.appointmentId}-${config.sectionTag}-${Date.now()}.wav`;
      const file = new File([new Blob([wavBuffer])], filename, {
        type: 'audio/wav',
      });
      const uploadResult = await supabaseStorageService.uploadAudioFile(
        file,
        {
          locationId: config.locationId,
          patientId: config.patientId,
          appointmentId: config.appointmentId,
          sectionTag: config.sectionTag,
          scribeJobId: config.scribeJobId,
        }
      );
      console.log(
        '[PublicRecording] background audio upload:',
        uploadResult.success
          ? `OK (${uploadResult.signedUrl?.slice(0, 60)}…)`
          : `FAILED — ${uploadResult.error}`
      );
    } catch (err) {
      console.error(
        '[PublicRecording] background audio upload threw:',
        err
      );
    }
  }

  // -------- Waveform support ---------------------------------------------

  private startAudioLevelDetection(): void {
    if (!this.analyser || !this.timeDomainArray) return;
    const detect = () => {
      if (!this.isRecording || this.isPaused) {
        this.audioLevelAnimationFrame = null;
        return;
      }
      if (this.analyser && this.timeDomainArray) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        this.analyser.getFloatTimeDomainData(this.timeDomainArray as any);
      }
      let sum = 0;
      let max = 0;
      let count = 0;
      for (let i = 0; i < this.timeDomainArray!.length; i++) {
        const sample = Math.abs(this.timeDomainArray![i]);
        max = Math.max(max, sample);
        sum += sample * sample;
        count++;
      }
      const rms = Math.sqrt(sum / count);
      let level = 0;
      if (rms > 0.0001 || max > 0.001) {
        const rmsDb = 20 * Math.log10(Math.max(0.0001, rms));
        const peakDb = 20 * Math.log10(Math.max(0.001, max));
        const rmsN = Math.max(0, Math.min(1, (rmsDb + 80) / 80));
        const peakN = Math.max(0, Math.min(1, (peakDb + 60) / 60));
        const combined = rmsN * 0.8 + peakN * 0.2;
        if (combined < 0.1) level = Math.pow(combined * 3, 0.7);
        else if (combined < 0.3) level = Math.pow(combined * 1.5, 0.8);
        else level = Math.pow(combined, 0.9);
        if (level < 0.02 && (rms > 0.0005 || max > 0.005)) {
          level = 0.02 + rms * 50;
        }
        level = Math.max(0, Math.min(1, level));
      }
      this.audioLevelListeners.forEach((cb) => cb(level));
      this.audioLevelAnimationFrame = requestAnimationFrame(detect);
    };
    this.audioLevelAnimationFrame = requestAnimationFrame(detect);
  }

  private stopAudioLevelDetection(): void {
    if (this.audioLevelAnimationFrame !== null) {
      cancelAnimationFrame(this.audioLevelAnimationFrame);
      this.audioLevelAnimationFrame = null;
    }
  }

  onAudioLevelChange(callback: (level: number) => void): () => void {
    this.audioLevelListeners.push(callback);
    return () => {
      const index = this.audioLevelListeners.indexOf(callback);
      if (index > -1) this.audioLevelListeners.splice(index, 1);
    };
  }

  isReadyForAudioLevels(): boolean {
    return (
      this.audioContext !== null &&
      this.analyser !== null &&
      this.timeDomainArray !== null &&
      this.isRecording &&
      !this.isPaused
    );
  }

  getRecordingState(): { isRecording: boolean; isPaused: boolean } {
    return { isRecording: this.isRecording, isPaused: this.isPaused };
  }

  // -------- Teardown -----------------------------------------------------

  private cleanup(): void {
    this.stopAudioLevelDetection();

    if (this.keepAliveTimer) {
      clearInterval(this.keepAliveTimer);
      this.keepAliveTimer = null;
    }

    if (this.processor) {
      try {
        this.processor.disconnect();
      } catch {
        /* noop */
      }
      this.processor.onaudioprocess = null;
      this.processor = null;
    }

    if (this.source) {
      try {
        this.source.disconnect();
      } catch {
        /* noop */
      }
      this.source = null;
    }

    if (this.analyser) {
      try {
        this.analyser.disconnect();
      } catch {
        /* noop */
      }
      this.analyser = null;
    }

    if (this.audioContext) {
      this.audioContext.close().catch(() => {
        /* noop */
      });
      this.audioContext = null;
    }

    if (this.stream) {
      this.stream.getTracks().forEach((track) => track.stop());
      this.stream = null;
    }

    if (this.socket) {
      this.socket.removeEventListener('message', this.handleSocketMessage);
      this.socket.removeEventListener('close', this.handleSocketClose);
      try {
        this.socket.close(1000, 'client stop');
      } catch {
        /* noop */
      }
      this.socket = null;
    }

    this.timeDomainArray = null;
    this.audioLevelListeners = [];
    this.transcriptListeners = [];
  }
}

// -------- PCM helpers -----------------------------------------------------

function downsampleFloat32(
  input: Float32Array,
  inputRate: number,
  outputRate: number
): Float32Array {
  if (outputRate === inputRate) return input;
  if (outputRate > inputRate) throw new Error('Upsampling not supported');
  const ratio = inputRate / outputRate;
  const newLength = Math.floor(input.length / ratio);
  const result = new Float32Array(newLength);
  let offsetResult = 0;
  let offsetInput = 0;
  while (offsetResult < newLength) {
    const nextOffsetInput = Math.floor((offsetResult + 1) * ratio);
    let accum = 0;
    let count = 0;
    for (let i = offsetInput; i < nextOffsetInput && i < input.length; i++) {
      accum += input[i];
      count++;
    }
    result[offsetResult] = count > 0 ? accum / count : 0;
    offsetResult++;
    offsetInput = nextOffsetInput;
  }
  return result;
}

function floatTo16BitPCM(float32Array: Float32Array): ArrayBuffer {
  const buffer = new ArrayBuffer(float32Array.length * 2);
  const view = new DataView(buffer);
  let offset = 0;
  for (let i = 0; i < float32Array.length; i++, offset += 2) {
    const s = Math.max(-1, Math.min(1, float32Array[i]));
    view.setInt16(offset, s < 0 ? s * 0x8000 : s * 0x7fff, true);
  }
  return buffer;
}

function pcm16MonoToWav(samples: Int16Array, sampleRate: number): ArrayBuffer {
  const numChannels = 1;
  const bytesPerSample = 2;
  const byteRate = sampleRate * numChannels * bytesPerSample;
  const blockAlign = numChannels * bytesPerSample;
  const dataLength = samples.byteLength;
  const buffer = new ArrayBuffer(44 + dataLength);
  const view = new DataView(buffer);
  const writeString = (off: number, s: string) => {
    for (let i = 0; i < s.length; i++) {
      view.setUint8(off + i, s.charCodeAt(i));
    }
  };
  writeString(0, 'RIFF');
  view.setUint32(4, 36 + dataLength, true);
  writeString(8, 'WAVE');
  writeString(12, 'fmt ');
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true);
  view.setUint16(22, numChannels, true);
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, byteRate, true);
  view.setUint16(32, blockAlign, true);
  view.setUint16(34, 16, true);
  writeString(36, 'data');
  view.setUint32(40, dataLength, true);
  const pcmView = new DataView(buffer, 44);
  for (let i = 0; i < samples.length; i++) {
    pcmView.setInt16(i * 2, samples[i], true);
  }
  return buffer;
}
